import { CalendarEvent, ShiftsGroupEvent, User } from '@nextforce/api'
import { Divider, Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { countBy, times } from 'lodash'
import React, { useMemo } from 'react'
import { View } from 'react-native'
import { Card } from 'shared/components/card'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { StackedUserAvatars } from 'shared/components/user/stacked_user_avatars'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { Time } from './time'

interface Props {
  event: ShiftsGroupEvent
  expanded: boolean
  groupsCount: number
  onEventExpand(event: CalendarEvent): void
}

export const EVENT_SUMMARY_HEIGHT = 100
export const EVENT_SUMMARY_STACK_OFFSET = 5

const TOP_SUMMARY_COUNT = 2
export const SUMMARY_AVATARS_MAX_COUNT = 4

export const COLLAPSIBLE_COUNT = 3

export function ShiftsGroupEventSummaryView({
  event,
  expanded,
  groupsCount,
  onEventExpand,
}: Props) {
  const collapsedCount = getCollpasedShiftCount(expanded, groupsCount)
  const styles = useStyles({ collapsedCount })
  const { theme } = useTheme()

  const [positionCountText, userPositionList] = useMemo(() => {
    const anyPosition = t('anyPosition')
    const positionCounts = countBy(
      event.shifts,
      shift => shift.position ?? anyPosition
    )

    const sortedPositionCounts = Object.entries(positionCounts)
      .sort(([position1, count1], [position2, count2]) => {
        if (count2 > count1) {
          return 1
        }

        if (count2 < count1) {
          return -1
        }

        if (position1 === anyPosition && position2 === anyPosition) {
          return 0
        }

        if (position2 === anyPosition) {
          return -1
        }

        if (position1 === anyPosition) {
          return 1
        }

        return 0
      })
      .slice(0, TOP_SUMMARY_COUNT)

    const topCountTotal = sortedPositionCounts.reduce(
      (sum, [, count]) => sum + count,
      0
    )

    let text = sortedPositionCounts
      .map(([position, count]) => `${count} ${position}`)
      .join(', ')

    const extra = event.shifts.length - topCountTotal

    if (extra > 0) {
      text += ` & ${extra} More`
    }

    const topPositions = sortedPositionCounts.map(
      positionCount => positionCount[0]
    )

    const userPositions = event.shifts
      .filter(shift =>
        shift.position
          ? shift.position in positionCounts
          : anyPosition in positionCounts
      )
      .map(shift => {
        return {
          position: shift.position ?? anyPosition,
          user: shift.user,
        }
      })
      .sort((shift1, shift2) => {
        const indexOfPosition1 = topPositions.indexOf(shift1.position)
        const indexOfPosition2 = topPositions.indexOf(shift2.position)
        if (
          shift1.position === anyPosition &&
          shift2.position === anyPosition
        ) {
          return 0
        }
        if (shift1.position === anyPosition) {
          return 1
        }

        if (shift2.position === anyPosition) {
          return -1
        }

        if (indexOfPosition1 === -1) {
          return 1
        }

        if (indexOfPosition2 === -1) {
          return -1
        }

        return indexOfPosition1 - indexOfPosition2
      })
      .slice(0, SUMMARY_AVATARS_MAX_COUNT + 1)

    return [text, userPositions]
  }, [event.shifts])

  return (
    <PressableOpacity
      style={styles.container}
      onPress={() => onEventExpand(event)}
      disabled={groupsCount === 0}
    >
      {times(collapsedCount, index => (
        <Card
          key={index}
          style={[styles.stack, { top: index * EVENT_SUMMARY_STACK_OFFSET }]}
        />
      ))}

      <Card style={styles.card}>
        <View style={styles.outer}>
          <View style={styles.outerRow}>
            <Time event={event} hideDivider />
            <View style={styles.counts}>
              <Text style={styles.countText} numberOfLines={1}>
                {positionCountText}
              </Text>
            </View>
          </View>
          <Divider />
        </View>
        <View style={styles.inner}>
          <View style={styles.summary}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{event.shifts.length}</Text>
            </View>
          </View>
          <Divider />
          <AvatarRow userPositionList={userPositionList} />
          <View style={styles.caretContainer}>
            <View style={styles.caret}>
              <Icon
                name={expanded ? 'angle-down' : 'angle-right'}
                type="font-awesome-5"
                color={theme.colors?.grey2}
                size={20}
                solid
              />
            </View>
          </View>
        </View>
      </Card>
    </PressableOpacity>
  )
}

export function getCollpasedShiftCount(expanded: boolean, groupsCount: number) {
  return expanded || !groupsCount ? 0 : COLLAPSIBLE_COUNT
}

interface AvatarRowProps {
  userPositionList: {
    position: string
    user?: User
  }[]
}

function AvatarRow({ userPositionList }: AvatarRowProps) {
  const styles = useStyles()
  return (
    <View style={styles.avatarRow}>
      {userPositionList.map((userPosition, index) => (
        <View style={styles.avatar} key={String(index)}>
          {index === SUMMARY_AVATARS_MAX_COUNT ? (
            <StackedUserAvatars
              user={userPosition.user}
              size={styles.badge.height}
            />
          ) : (
            <UserAvatar user={userPosition.user} size={40} />
          )}
        </View>
      ))}
    </View>
  )
}

const useStyles = makeStyles((theme, params: { collapsedCount: number }) => ({
  container: {
    height:
      EVENT_SUMMARY_HEIGHT + EVENT_SUMMARY_STACK_OFFSET * params.collapsedCount,
  },
  card: {
    height: EVENT_SUMMARY_HEIGHT,
    marginTop: EVENT_SUMMARY_STACK_OFFSET * params.collapsedCount,
  },
  outer: {
    flex: 0.4,
  },
  outerRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inner: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 2,
  },
  summary: {
    width: 60,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  badge: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    backgroundColor: theme.colors?.grey2,
    height: 21,
    minWidth: 21,
    marginLeft: 5,
  },
  caretContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  caret: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    height: 21,
    minWidth: 21,
    marginLeft: 5,
  },

  badgeText: {
    fontSize: 14,
    color: 'white',
  },
  counts: {
    flex: 1,
    alignItems: 'center',
    marginLeft: 5,
  },
  countText: {
    fontSize: 13,
  },
  stack: {
    position: 'absolute',
    height: EVENT_SUMMARY_HEIGHT - EVENT_SUMMARY_STACK_OFFSET * 2,
    width: '100%',
  },
  avatarRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    margin: 5,
  },
}))
